/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Logger utility that respects REACT_APP_DEBUG environment variable.
 *
 * When REACT_APP_DEBUG is set to 'true' or '1', console logging is enabled.
 * In production (when REACT_APP_DEBUG is false/undefined), all logging is muted.
 *
 * This module replaces the global console object to ensure no accidental
 * logging in production environments.
 */

type ConsoleMethods =
  | 'log' | 'info' | 'debug' | 'warn' | 'error' | 'trace' | 'dir' | 'table'
  | 'group' | 'groupEnd' | 'groupCollapsed' | 'time' | 'timeEnd' | 'timeLog'
  | 'clear' | 'count' | 'countReset' | 'assert';

// Store original console methods before overwriting
const originalConsole: Partial<Record<ConsoleMethods, (...args: unknown[]) => void>> = {};

/**
 * No-op function for muted console in production.
 * Replaces console methods when debug mode is disabled to prevent accidental logging.
 */
const noop = (): void => {
  // Intentionally empty - muted in production
};

/**
 * Check if debug mode is enabled based on REACT_APP_DEBUG environment variable.
 * Returns true if REACT_APP_DEBUG is set to 'true' or '1'.
 */
export function isDebugEnabled(): boolean {
  const debugValue = import.meta.env.REACT_APP_DEBUG;
  return debugValue === 'true' || debugValue === '1' || debugValue === true;
}

/**
 * Initialize the logger by replacing the global console object.
 * This should be called as early as possible in the application lifecycle.
 *
 * - In debug mode (REACT_APP_DEBUG=true): Console methods work normally
 * - In production mode (REACT_APP_DEBUG=false/undefined): Console methods are muted
 */
export function initializeLogger(): void {
  const methodsToOverride: ConsoleMethods[] = [
    'log',
    'info',
    'debug',
    'warn',
    'error',
    'trace',
    'dir',
    'table',
    'group',
    'groupEnd',
    'groupCollapsed',
    'time',
    'timeEnd',
    'timeLog',
    'clear',
    'count',
    'countReset',
    'assert',
  ];

  // Store original methods
  methodsToOverride.forEach((method) => {
    const original = console[method] as (...args: unknown[]) => void;
    if (typeof original === 'function') {
      originalConsole[method] = original.bind(console);
    }
  });

  const debugEnabled = isDebugEnabled();

  if (!debugEnabled) {
    // In production, replace console methods with no-op
    methodsToOverride.forEach((method) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (console as any)[method] = noop;
    });
  }

  // Log initialization status only if debug is enabled
  if (debugEnabled) {
    console.info('[Logger] Debug mode enabled - console logging is active');
  }
}

/**
 * Restore original console methods.
 * Useful for testing or when you need to temporarily enable logging.
 */
export function restoreConsole(): void {
  Object.entries(originalConsole).forEach(([method, originalMethod]) => {
    if (originalMethod) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (console as any)[method] = originalMethod;
    }
  });
}

/**
 * Logger instance that respects REACT_APP_DEBUG.
 * Use this for explicit logging that should respect the debug flag.
 */
export const logger = {
  log: (...args: unknown[]): void => {
    if (isDebugEnabled()) {
      originalConsole.log?.(...args);
    }
  },
  info: (...args: unknown[]): void => {
    if (isDebugEnabled()) {
      originalConsole.info?.(...args);
    }
  },
  debug: (...args: unknown[]): void => {
    if (isDebugEnabled()) {
      originalConsole.debug?.(...args);
    }
  },
  warn: (...args: unknown[]): void => {
    if (isDebugEnabled()) {
      originalConsole.warn?.(...args);
    }
  },
  error: (...args: unknown[]): void => {
    if (isDebugEnabled()) {
      originalConsole.error?.(...args);
    }
  },
  trace: (...args: unknown[]): void => {
    if (isDebugEnabled()) {
      originalConsole.trace?.(...args);
    }
  },
};
